"use client"

import styled from "@emotion/styled"
import { Box, Button, Typography } from "@mui/material"
import React from "react"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import { iconsMap } from "../../public/icons/iconsMap"
import { extractEmail, extractPhone } from "../../utils/extractContactInfo"
import ConditionalCollapse from "../common/bit/ConditionalCollapse"
import { useIsDesktop } from "../common/bit/MediaQueries"

const StyledItem = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: ${sizes.baseDouble};
  border-radius: ${sizes.baseDouble};
  border: 1px solid ${colors.red};
  background: rgba(255, 255, 255, 0.9);
  box-shadow: -5.759px 11.517px 43.189px 0px rgba(2, 1, 64, 0.12);
  backdrop-filter: blur(8px);
  width: 100%;
  color: ${colors.black};
  ${mediaQueries.desktopM} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: ${sizes.baseTriple};
    > * {
      display: flex;
      box-sizing: border-box;
      flex: 1;
      &:first-child {
        flex: 1.4;
      }
    }
  }
  &.circle-image-title {
    background: ${colors.red};
    h4 {
      color: ${colors.white};
    }
  }
`

const StyledIconTypography = styled(Typography)`
  display: flex;
  align-items: center;
  color: ${colors.red};
  gap: 12px;
  ${mediaQueries.desktopM} {
    gap: 20px;
  }
  ${mediaQueries.tabletDown} {
    margin-inline-end: auto;
    font-size: 16px;
  }
`

const StyledImageWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  object-fit: cover;
  border: 2px solid ${colors.red};
  background-color: ${colors.lightPink};
  flex: 0 0 auto;
  svg {
    width: 24px;
    height: 24px;
  }
  ${mediaQueries.desktopM} {
    width: 56px;
    height: 56px;
    svg {
      width: 32px;
      height: 32px;
    }
  }
`

const StyledImage = styled("img")`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`

const StyledButton = styled(Button)`
  padding: 10px 18px;
  ${mediaQueries.tabletDown} {
    position: absolute;
    justify-content: flex-end;
    height: 100%;
    background: transparent;
    border: none;
    padding: 0;
    right: ${sizes.baseSingle};
  }
`

const StyledButtonWrapper = styled(Box)`
  justify-content: flex-end;
  width: fit-content;
`

const StyledMapLinks = styled(Box)`
  display: flex;
  gap: 10px;
`

const StyledMapLink = styled("a")`
  display: flex;
  align-items: center;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

const StyledContactInfo = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`

const StyledMobileTitle = styled(Typography)`
  ${mediaQueries.tabletDown} {
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: -0.16px;
    color: ${colors.black};
    margin-top: ${sizes.baseTriple};
  }
`
const StyledAddress = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  ${mediaQueries.tabletDown} {
    justify-content: space-between;
  }
`

const StyledContactLink = styled("a")`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.black};
  text-decoration: none;
  font-size: 16px;
  gap: ${sizes.baseSingle};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  flex: 0 0 auto;
  &:hover {
    text-decoration: underline;
  }
`

const IconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  svg {
    width: 24px;
    height: auto;
    flex: 0 0 auto;
    object-fit: cover;
    fill: ${colors.black};
    &.phone {
      stroke: ${colors.black};
    }
  }
  ${mediaQueries.tabletDown} {
    background-color: ${colors.lighterRed};
    border-radius: 50%;
    padding: 8px;
    svg {
      fill: ${colors.white};
      width: 11px;
      height: 11px;
      &.phone {
        stroke: ${colors.white};
      }
    }
  }
`
const StyledTitleTitle = styled(Typography)`
  margin-top: ${sizes.baseSingle};
`

const StyledCollapseContent = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${sizes.baseSingle};
  border-top: 1px solid ${colors.borderGray};
  padding-top: ${sizes.baseSingle};
`

const StyledConditionalCollapse = styled(ConditionalCollapse)`
  width: 100%;
  &.Mui-expanded {
    .title-phone,
    .title-email {
      height: 0;
      padding: 0;
      margin: 0;
    }
  }
`

const StyledMobileCollapseTitles = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${sizes.baseSingle};
  color: ${colors.black};
  ${mediaQueries.tabletDown} {
    margin-inline-end: ${sizes.baseDouble};
    width: 100%;
  }
`

interface Address {
  fullAddress?: string
  city?: string
  location?: {
    latitude?: string | number
    longitude?: string | number
  }
}

interface ContactInfo {
  emailAddress?: string
  phoneNumber?: string
}

interface Column {
  title?: string
  marketingName?: string
  contactName?: string
  specialty?: string
  address?: Address
  contactInfo?: string | ContactInfo
}

interface ButtonProps {
  href?: string
  text?: string
}

export interface CircleImageLinkItemProps {
  icon?: {
    src: string
    alt: string
  }
  columns: Column[]
  button?: ButtonProps
  itemKey: string
  isTitle?: boolean
  titles?: { title: string }[]
  mobileTitles?: { title: string }[]
}

interface ContactInfoResult {
  email: string | null
  phoneNumber: string | null
  rawContent: string | ContactInfo | null
}

const CircleImageLinkItem = ({
  icon,
  columns,
  button,
  itemKey,
  isTitle = false,
  titles,
  mobileTitles,
}: CircleImageLinkItemProps) => {
  const ArrowIcon = iconsMap["left_red_arrow"]
  const WazeIcon = iconsMap["waze-icon"]
  const GoogleMapsIcon = iconsMap["google-maps-icon"]
  const EmailIcon = iconsMap["email"]
  const PhoneIcon = iconsMap["phone"]
  const DefaultIcon = iconsMap["default-CircleImageLinkListTable-icon"]
  const ArrowDownIcon = iconsMap["arrow-black-down"]
  const isDesktop = useIsDesktop()

  const getContactInfo = (
    contactInfo: string | ContactInfo | undefined,
  ): ContactInfoResult => {
    try {
      let email: string | null = null
      let phoneNumber: string | null = null

      if (contactInfo && typeof contactInfo === "object") {
        email = contactInfo.emailAddress || null
        phoneNumber = contactInfo.phoneNumber || null
      }

      if (!email && typeof contactInfo === "string") {
        email = extractEmail(contactInfo)
      }

      if (!phoneNumber && typeof contactInfo === "string") {
        phoneNumber = extractPhone(contactInfo)
      }

      return { email, phoneNumber, rawContent: contactInfo || null }
    } catch (error) {
      console.error("Error extracting contact information:", error)
      return { email: null, phoneNumber: null, rawContent: null }
    }
  }

  // Function to render column content based on type
  const renderColumnContent = (column: Column, index: number) => {
    const {
      title,
      marketingName,
      contactName,
      address,
      contactInfo,
      specialty,
    } = column

    switch (true) {
      case index === 0 && (!!title || !!marketingName):
        return (
          <StyledIconTypography
            variant={"h4"}
            key={`${itemKey}-column-title-${index}`}
          >
            <StyledImageWrapper>
              {icon ? (
                <StyledImage src={icon.src} alt={icon.alt} />
              ) : (
                <DefaultIcon />
              )}
            </StyledImageWrapper>
            {title || marketingName}
          </StyledIconTypography>
        )
      case !!address:
        return (
          <StyledAddress key={`${itemKey}-column-${index}`}>
            {(address?.fullAddress || address?.city) && (
              <Box key={`${itemKey}-column-address-${index}`}>
                {!isDesktop && (
                  <StyledMobileTitle variant={"body2"}>
                    {mobileTitles?.[index]?.title || ""}
                  </StyledMobileTitle>
                )}
                <Typography variant={"body2"}>
                  {address?.fullAddress || address?.city}
                </Typography>
              </Box>
            )}
            {address?.location &&
              address.location.latitude &&
              address.location.longitude && (
                <StyledMapLinks>
                  <StyledMapLink
                    href={`https://www.waze.com/ul?ll=${address.location.latitude},${address.location.longitude}&navigate=yes`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <WazeIcon />
                  </StyledMapLink>
                  <StyledMapLink
                    href={`https://www.google.com/maps/search/?api=1&query=${address.location.latitude},${address.location.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <GoogleMapsIcon />
                  </StyledMapLink>
                </StyledMapLinks>
              )}
          </StyledAddress>
        )
      case !!contactInfo:
        const { email, phoneNumber, rawContent } = getContactInfo(contactInfo)

        return (
          <StyledContactInfo key={`${itemKey}-column-${index}`}>
            {!isDesktop && (
              <StyledMobileTitle variant={"body2"}>
                {mobileTitles?.[index]?.title || ""}
              </StyledMobileTitle>
            )}
            {rawContent && typeof rawContent === "string" && (
              <Typography variant="body2">{rawContent}</Typography>
            )}
            {phoneNumber && (
              <StyledContactLink href={`tel:${phoneNumber}`}>
                <IconWrapper>
                  <PhoneIcon className="phone" />
                </IconWrapper>
                {phoneNumber}
              </StyledContactLink>
            )}

            {email && (
              <StyledContactLink
                href={`mailto:${email}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconWrapper>
                  <EmailIcon />
                </IconWrapper>
                {email}
              </StyledContactLink>
            )}
          </StyledContactInfo>
        )
      default:
        return (
          <React.Fragment key={`${itemKey}-column-address-${index}`}>
            {!isDesktop && (
              <StyledMobileTitle variant={"body2"}>
                {mobileTitles?.[index]?.title || ""}
              </StyledMobileTitle>
            )}
            <Typography variant={"body2"} key={`${itemKey}-column-${index}`}>
              {title || specialty || contactName || marketingName}
            </Typography>
          </React.Fragment>
        )
    }
  }

  // If this is a title item (only on desktop)
  if (isTitle && titles) {
    return (
      <StyledItem className="circle-image-title">
        {titles.map((currTitle, index) => {
          const { title } = currTitle
          const titleKey = `circle-image-title-${index}`
          return (
            <Typography key={titleKey} variant="h4">
              {title}
            </Typography>
          )
        })}
      </StyledItem>
    )
  }

  // For mobile view - use ConditionalCollapse
  if (!isDesktop) {
    // Extract all titles and marketingNames to display in the title
    const titlesList = columns
      ?.map(
        (column: any) =>
          column.title || column.marketingName || column.contactInfo,
      )
      .filter(Boolean) as (string | ContactInfo)[]

    return (
      <StyledConditionalCollapse
        key={itemKey}
        openIcon={<ArrowDownIcon />}
        closeIcon={<ArrowDownIcon />}
        link={button?.href}
        titleContent={
          <StyledMobileCollapseTitles className="mobile-collapse-titles">
            <StyledIconTypography variant="h4">
              <StyledImageWrapper>
                {icon ? (
                  <StyledImage src={icon.src} alt={icon.alt} />
                ) : (
                  <DefaultIcon />
                )}
              </StyledImageWrapper>
              {titlesList?.[0] && typeof titlesList[0] === "string"
                ? titlesList[0]
                : ""}
            </StyledIconTypography>
            {titlesList?.slice(1).map((title, idx) => {
              if (
                typeof title === "string" &&
                (title.includes("phoneNumber") || title.includes("email"))
              ) {
                const { email, phoneNumber } = getContactInfo(title)
                return (
                  <React.Fragment key={`${itemKey}-contact-${idx}`}>
                    {phoneNumber && (
                      <StyledContactLink href={`tel:${phoneNumber}`}>
                        <PhoneIcon />
                      </StyledContactLink>
                    )}
                    {email && (
                      <StyledContactLink
                        href={`mailto:${email}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <EmailIcon />
                      </StyledContactLink>
                    )}
                  </React.Fragment>
                )
              }

              // If title is an object (ContactInfo), extract email and phone
              if (typeof title === "object" && title !== null) {
                const { email, phoneNumber } = getContactInfo(title)
                return (
                  <React.Fragment key={`${itemKey}-contact-obj-${idx}`}>
                    {phoneNumber && (
                      <StyledContactLink
                        className="title-phone"
                        href={`tel:${phoneNumber}`}
                      >
                        <IconWrapper>
                          <PhoneIcon className="phone" />
                        </IconWrapper>
                      </StyledContactLink>
                    )}
                    {email && (
                      <StyledContactLink
                        href={`mailto:${email}`}
                        target="_blank"
                        className="title-email"
                        rel="noopener noreferrer"
                      >
                        <IconWrapper>
                          <EmailIcon />
                        </IconWrapper>
                      </StyledContactLink>
                    )}
                  </React.Fragment>
                )
              }

              // If it's a string that doesn't contain phoneNumber or email
              if (typeof title === "string") {
                return (
                  <StyledTitleTitle
                    key={`${itemKey}-title-title-${idx}`}
                    variant="body2"
                  >
                    {title}
                  </StyledTitleTitle>
                )
              }

              return null
            })}
          </StyledMobileCollapseTitles>
        }
      >
        {columns?.some(
          (column: any) => column.title || column.marketingName,
        ) && (
          <StyledCollapseContent>
            {columns?.map((column: any, colIndex: number) => {
              // Skip titles and marketingNames as they're already in the title
              if (column.title || column.marketingName) return null
              return renderColumnContent(column, colIndex)
            })}
          </StyledCollapseContent>
        )}
      </StyledConditionalCollapse>
    )
  }

  // For desktop view - original layout
  return (
    <StyledItem key={itemKey}>
      {columns?.map((column: any, colIndex: number) =>
        renderColumnContent(column, colIndex),
      )}
      {button && (
        <StyledButtonWrapper>
          <StyledButton variant="outlined" href={button?.href}>
            {isDesktop ? button?.text : ""}
            <ArrowIcon />
          </StyledButton>
        </StyledButtonWrapper>
      )}
    </StyledItem>
  )
}

export default CircleImageLinkItem
